import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchGithubRepo, submitUrl, type GithubRepoData } from '../lib/api.js';

function isGithubRepoUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname === 'github.com' && u.pathname.split('/').filter(Boolean).length === 2;
  } catch {
    return false;
  }
}

const README_STYLES = `
  .git-readme h1,.git-readme h2,.git-readme h3,.git-readme h4 {
    font-weight:600;margin-top:1.4em;margin-bottom:0.5em;color:#e2e8f0;
  }
  .git-readme h1{font-size:1.4em;border-bottom:1px solid rgba(255,255,255,0.08);padding-bottom:0.3em;}
  .git-readme h2{font-size:1.2em;border-bottom:1px solid rgba(255,255,255,0.06);padding-bottom:0.2em;}
  .git-readme h3{font-size:1.05em;}
  .git-readme p{margin:0.75em 0;color:#94a3b8;line-height:1.7;}
  .git-readme a{color:#818cf8;text-decoration:none;}
  .git-readme a:hover{text-decoration:underline;}
  .git-readme ul,.git-readme ol{padding-left:1.5em;margin:0.6em 0;color:#94a3b8;}
  .git-readme li{margin:0.3em 0;line-height:1.6;}
  .git-readme pre{background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.08);border-radius:6px;padding:1em;overflow-x:auto;margin:1em 0;}
  .git-readme code{background:rgba(255,255,255,0.07);border-radius:3px;padding:0.15em 0.4em;font-size:0.875em;font-family:ui-monospace,monospace;color:#e2e8f0;}
  .git-readme pre code{background:none;padding:0;font-size:0.825em;}
  .git-readme blockquote{border-left:3px solid rgba(99,102,241,0.5);margin:1em 0;padding:0.5em 1em;color:#64748b;background:rgba(99,102,241,0.04);border-radius:0 4px 4px 0;}
  .git-readme table{border-collapse:collapse;width:100%;margin:1em 0;}
  .git-readme th,.git-readme td{border:1px solid rgba(255,255,255,0.1);padding:0.5em 0.75em;text-align:left;color:#94a3b8;font-size:0.875em;}
  .git-readme th{background:rgba(255,255,255,0.04);color:#e2e8f0;font-weight:600;}
  .git-readme img{max-width:100%;border-radius:4px;margin:0.5em 0;}
  .git-readme hr{border:none;border-top:1px solid rgba(255,255,255,0.08);margin:1.5em 0;}
`;

export default function GitView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputUrl, setInputUrl] = useState('');
  const [repo, setRepo] = useState<GithubRepoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  const repoUrl = searchParams.get('repo') ?? '';

  useEffect(() => {
    if (!repoUrl) { setRepo(null); return; }
    setLoading(true);
    setError(null);
    setQueued(false);
    fetchGithubRepo(repoUrl)
      .then(setRepo)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load repository'))
      .finally(() => setLoading(false));
  }, [repoUrl]);

  function handleSearch() {
    const url = inputUrl.trim();
    if (!isGithubRepoUrl(url)) {
      setError('Enter a GitHub repository URL — e.g. https://github.com/owner/repo');
      return;
    }
    setError(null);
    setSearchParams({ repo: url });
  }

  async function handleAddToKnowledgeBase() {
    if (!repo) return;
    try {
      await submitUrl(repo.repoUrl);
      setQueued(true);
    } catch {
      setError('Failed to queue repository for ingestion');
    }
  }

  const updatedAgo = repo
    ? (() => {
        const days = Math.floor((Date.now() - new Date(repo.updatedAt).getTime()) / 86400000);
        if (days === 0) return 'today';
        if (days === 1) return 'yesterday';
        if (days < 30) return `${days}d ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months}mo ago`;
        return `${Math.floor(months / 12)}y ago`;
      })()
    : '';

  return (
    <>
      <style>{README_STYLES}</style>
      <div className="max-w-4xl mx-auto px-6 py-6">

        {/* Search bar */}
        <div className="flex gap-2 mb-6">
          <input
            type="url"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="https://github.com/owner/repo"
            className="flex-1 bg-obsidian-bg-secondary border border-obsidian-border rounded-lg px-4 py-2.5 text-sm text-obsidian-text placeholder:text-obsidian-text-faint focus:outline-none focus:border-obsidian-accent transition-colors"
          />
          <button
            onClick={handleSearch}
            disabled={!inputUrl.trim()}
            className="px-4 py-2.5 bg-obsidian-accent text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
          >
            View
          </button>
        </div>

        {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

        {!repoUrl && !loading && (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">⎇</div>
            <p className="text-sm text-obsidian-text-muted">Enter a GitHub repository URL above</p>
            <p className="text-xs text-obsidian-text-faint mt-1">e.g. https://github.com/anthropics/claude-code</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-20">
            <div className="text-2xl mb-3 animate-pulse">⎇</div>
            <p className="text-sm text-obsidian-text-muted">Loading repository...</p>
          </div>
        )}

        {repo && !loading && (
          <div>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-obsidian-text leading-tight break-all">
                    {repo.fullName}
                  </h1>
                  {repo.description && (
                    <p className="text-sm text-obsidian-text-muted mt-1.5 leading-relaxed">
                      {repo.description}
                    </p>
                  )}
                  {repo.homepage && (
                    <a
                      href={repo.homepage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-obsidian-accent hover:underline mt-1 inline-block"
                    >
                      {repo.homepage}
                    </a>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={handleAddToKnowledgeBase}
                    disabled={queued}
                    className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all"
                    style={
                      queued
                        ? { background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', color: '#10b981' }
                        : { background: 'rgba(99,102,241,0.15)', border: '1px solid #6366f1', color: '#818cf8' }
                    }
                  >
                    {queued ? '✓ Queued' : '+ Add to KB'}
                  </button>
                  <a
                    href={repo.repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg transition-all"
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#6b7280' }}
                  >
                    GitHub ↗
                  </a>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-xs text-obsidian-text-muted mt-3 flex-wrap">
                <span>⭐ {repo.stars.toLocaleString()}</span>
                <span>🍴 {repo.forks.toLocaleString()}</span>
                {repo.openIssues > 0 && <span>● {repo.openIssues.toLocaleString()} issues</span>}
                {repo.language && (
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: '#64748b' }} />
                    {repo.language}
                  </span>
                )}
                <span>Updated {updatedAgo}</span>
              </div>

              {/* Topics */}
              {repo.topics.length > 0 && (
                <div className="flex gap-1.5 flex-wrap mt-3">
                  {repo.topics.map((t) => (
                    <span
                      key={t}
                      className="text-xs px-2.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8' }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: '1.5rem' }} />

            {/* README — HTML is sanitized server-side (scripts/event handlers stripped) */}
            {repo.readmeHtml ? (
              // eslint-disable-next-line react/no-danger
              <div
                className="git-readme text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: repo.readmeHtml }}
              />
            ) : (
              <p className="text-sm text-obsidian-text-faint italic">No README available.</p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
